// The PopCart map: the quiet tracking style (owner-approved redesign, 2026-10-07,
// docs/design-review/tracking-redesign), the ONE map style every PopCart map uses: the customer's
// live order map and address picker, the rider's job and navigation maps, and the Ops zone map.
// Cream land, white roads on a soft warm edge (no Ink casing), tinted parks and water, no
// buildings, small muted road labels, so the route and the pins are the loudest things, as Grab
// and Lalamove do. Native maps (docs/NATIVE-MAPS-CONTRACT.md §1) fill it in like Google's: place
// icons with names in their category colour, transit stations, one-way arrows and house numbers,
// each at the zoom where it helps. Every colour comes from @popcart/tokens.
//
// The icon layers need the kit's images on the map: `<Images images={mapIconImages} />`.
//
// A MapLibre style over OpenMapTiles-schema vector tiles; serve it through `styleFromApi` so the
// tiles and glyphs come from our API. Overlays: the route with `trackingRoute`, the pins with the
// kit's <MapPin /> and the rider with <RiderMarker />. (The 2026-10-06 Bold Pop card style and its
// dashed straight route were retired 2026-10-09: one map design everywhere.)
import type { LayerSpecification, StyleSpecification } from '@maplibre/maplibre-gl-style-spec';
import { tokens } from '@popcart/tokens';
import { placeLabelColourExpression, placeLabelColours, poiCategoryExpression } from './native';

const c = tokens.colour;
const ink = c.core.ink;
const cream = c.core.cream;
const white = c.extended.white;
const sky = c.category.mart;
const kiwi = c.category.fresh;

/** OpenFreeMap: the vector tiles and glyphs the style reads. */
export const MAP_TILES = {
  source: 'https://tiles.openfreemap.org/planet',
  glyphs: 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
} as const;

/** The label face the tile server ships (Archivo is not served as map glyphs). */
const LABEL_FONT = ['Noto Sans Regular'];
const LABEL_FONT_BOLD = ['Noto Sans Bold'];

type Layer = LayerSpecification;

const roadClasses = {
  major: ['motorway', 'trunk', 'primary'],
  mid: ['secondary', 'tertiary'],
  minor: ['minor', 'service', 'track', 'path'],
};

/** Road width by zoom: [z, width] pairs for an interpolate expression. */
const width = (stops: Array<[number, number]>) =>
  ['interpolate', ['exponential', 1.4], ['zoom'], ...stops.flat()] as unknown as number;

const edge = c.neutral.disabledFill;
const quietLabel = c.neutral.ink300;

/**
 * Basemap places (native maps, docs/NATIVE-MAPS-CONTRACT.md §1), the way Google draws them: a round
 * icon in the category colour with the name beside it in the same hue on a Cream halo. Icons are
 * the kit's `poi-<PlaceCategory>` images (register `mapIconImages` on the map). Better-known places
 * (lower rank) win collisions, and names drop out before icons do.
 */
function poi(id: string, opts: { minzoom: number; maxzoom?: number; maxRank?: number }): Layer {
  const filter = opts.maxRank == null ? ['!', isStation] : ['all', ['!', isStation], ['<=', ['get', 'rank'], opts.maxRank]];
  return {
    id,
    type: 'symbol',
    source: 'openmaptiles',
    'source-layer': 'poi',
    minzoom: opts.minzoom,
    ...(opts.maxzoom == null ? {} : { maxzoom: opts.maxzoom }),
    filter: filter as never,
    layout: {
      'icon-image': ['concat', 'poi-', poiCategoryExpression],
      'icon-size': POI_SCALE,
      'symbol-sort-key': ['get', 'rank'],
      'text-field': ['get', 'name'],
      'text-font': LABEL_FONT,
      'text-size': 10,
      'text-max-width': 8,
      'text-variable-anchor': ['left', 'right'],
      'text-radial-offset': 1.1,
      'text-justify': 'auto',
      'text-optional': true,
    },
    paint: {
      'text-color': placeLabelColourExpression(poiCategoryExpression),
      'text-halo-color': cream,
      'text-halo-width': 1.5,
    },
  } as Layer;
}

/** Quiet map: place icons at 80 % of their drawn 20 pt. */
const POI_SCALE = 0.8;

/** Stations (rail, ferry, bus terminals) get their own layer: the transit icon from z13, any rank. */
const isStation = ['any', ['==', ['get', 'class'], 'railway'], ['==', ['get', 'class'], 'ferry_terminal'], ['==', ['get', 'subclass'], 'bus_station']];

/** A road class as white on a soft warm edge (casing = the edge layer, drawn first). */
function road(id: string, classes: string[], casing: boolean, stops: Array<[number, number]>): Layer {
  return {
    id: `${id}${casing ? '-casing' : ''}`,
    type: 'line',
    source: 'openmaptiles',
    'source-layer': 'transportation',
    filter: ['all', ['in', ['get', 'class'], ['literal', classes]], ['!=', ['get', 'brunnel'], 'tunnel']],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: casing
      ? { 'line-color': edge, 'line-width': width(stops.map(([z, w]) => [z, w + 2])) }
      : { 'line-color': white, 'line-width': width(stops) },
  };
}

/** Pin colours (§15): the drop-off / home pin is Pop Yellow with an Ink edge; the rider Cart Blue. */
export const mapPinColours = { home: c.core.volt, rider: c.core.ultra, shop: c.category.grill, edge: ink } as const;

/**
 * Pins are native markers, so map labels don't know they're there and can print over them. A
 * keep-out layer puts an invisible glyph at every pin point; drawn above the style's labels it is
 * placed first, and MapLibre's collision moves the labels out of its way.
 */
export const mapKeepout = {
  /** Space between the map credit and the card edge. */
  creditInset: 6,
  layer: (id: string, pinSize: number) =>
    ({
      id,
      type: 'symbol',
      layout: {
        'text-field': 'O',
        'text-font': LABEL_FONT_BOLD,
        'text-size': Math.round(pinSize * 1.4),
        'text-allow-overlap': true,
        'text-ignore-placement': false,
        'text-padding': 4,
      },
      paint: { 'text-opacity': 0 },
    }) as const,
};

const trackingLayers: Layer[] = [
  { id: 'background', type: 'background', paint: { 'background-color': cream } },
  {
    id: 'park',
    type: 'fill',
    source: 'openmaptiles',
    'source-layer': 'park',
    paint: { 'fill-color': kiwi, 'fill-opacity': 0.3 },
  },
  {
    id: 'water',
    type: 'fill',
    source: 'openmaptiles',
    'source-layer': 'water',
    paint: { 'fill-color': sky, 'fill-opacity': 0.45 },
  },
  road('road-minor', roadClasses.minor, true, [[13, 0.5], [18, 9]]),
  road('road-mid', roadClasses.mid, true, [[10, 0.75], [18, 14]]),
  road('road-major', roadClasses.major, true, [[6, 0.75], [18, 20]]),
  road('road-minor', roadClasses.minor, false, [[13, 0.5], [18, 9]]),
  road('road-mid', roadClasses.mid, false, [[10, 0.75], [18, 14]]),
  road('road-major', roadClasses.major, false, [[6, 0.75], [18, 20]]),
  // 'road-label' stays the first label layer: overlays placed before it (mapTraffic.beforeId) sit
  // over the road fill and under every label.
  {
    id: 'road-label',
    type: 'symbol',
    source: 'openmaptiles',
    'source-layer': 'transportation_name',
    minzoom: 13,
    filter: ['in', ['get', 'class'], ['literal', [...roadClasses.major, ...roadClasses.mid]]],
    layout: { 'symbol-placement': 'line', 'text-field': ['get', 'name'], 'text-font': LABEL_FONT, 'text-size': 10 },
    paint: { 'text-color': quietLabel, 'text-halo-color': white, 'text-halo-width': 1.5 },
  },
  {
    id: 'road-label-minor',
    type: 'symbol',
    source: 'openmaptiles',
    'source-layer': 'transportation_name',
    minzoom: 15,
    filter: ['in', ['get', 'class'], ['literal', roadClasses.minor]],
    layout: { 'symbol-placement': 'line', 'text-field': ['get', 'name'], 'text-font': LABEL_FONT, 'text-size': 10 },
    paint: { 'text-color': quietLabel, 'text-halo-color': white, 'text-halo-width': 1.5 },
  },
  {
    // One-way arrows along the road (OpenMapTiles `oneway`: 1 = the way the line is drawn, -1 =
    // against it). They never push a label away.
    id: 'oneway',
    type: 'symbol',
    source: 'openmaptiles',
    'source-layer': 'transportation',
    minzoom: 16,
    filter: ['in', ['get', 'oneway'], ['literal', [1, -1]]],
    layout: {
      'symbol-placement': 'line',
      'symbol-spacing': 120,
      'icon-image': 'oneway',
      'icon-size': ['interpolate', ['linear'], ['zoom'], 16, 0.75, 19, 1],
      'icon-rotate': ['match', ['get', 'oneway'], -1, 180, 0],
      'icon-rotation-alignment': 'map',
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  },
  {
    id: 'housenumber',
    type: 'symbol',
    source: 'openmaptiles',
    'source-layer': 'housenumber',
    minzoom: 18,
    layout: { 'text-field': ['get', 'housenumber'], 'text-font': LABEL_FONT, 'text-size': 10 },
    paint: { 'text-color': quietLabel, 'text-halo-color': cream, 'text-halo-width': 1.5 },
  },
  // Places: the best-known (rank ≤ 12) from z15, every one from z17. Filipino addresses lean on
  // them ("beside the 7-Eleven"). One spec in zoom bands; 'landmark-label' keeps its map-accuracy id.
  poi('poi', { minzoom: 15, maxzoom: 16, maxRank: 12 }),
  poi('landmark-label', { minzoom: 16, maxzoom: 17, maxRank: 12 }),
  poi('poi-detail', { minzoom: 17 }),
  {
    id: 'transit-station',
    type: 'symbol',
    source: 'openmaptiles',
    'source-layer': 'poi',
    minzoom: 13,
    filter: isStation as never,
    layout: {
      'icon-image': 'poi-transit',
      'icon-size': POI_SCALE,
      'text-field': ['get', 'name'],
      'text-font': LABEL_FONT_BOLD,
      'text-size': 10,
      'text-max-width': 8,
      'text-variable-anchor': ['left', 'right'],
      'text-radial-offset': 1.1,
      'text-justify': 'auto',
      'text-optional': true,
    },
    paint: { 'text-color': placeLabelColours.transit, 'text-halo-color': cream, 'text-halo-width': 1.5 },
  },
];

/** The PopCart map style (style spec v8). Pass it through `styleFromApi`, then as `mapStyle`. */
export const popcartTrackingStyle: StyleSpecification = {
  version: 8,
  name: 'PopCart tracking',
  glyphs: MAP_TILES.glyphs,
  sources: {
    openmaptiles: { type: 'vector', url: MAP_TILES.source, attribution: '© OpenFreeMap © OpenMapTiles © OpenStreetMap contributors' },
  },
  layers: trackingLayers,
};
export const popcartTrackingStyleJSON: string = JSON.stringify(popcartTrackingStyle);

/** The live route: a solid Ink line on a white edge; the travelled part is a faint dotted trail. */
export const trackingRoute = {
  casing: { 'line-color': white, 'line-width': 9 },
  line: { 'line-color': ink, 'line-width': 5 },
  trail: { 'line-color': c.neutral.ink300, 'line-width': 3, 'line-dasharray': [0.5, 2.5] },
} as const;

// ── Self-hosted tiles (map architecture, 2026-10-07) ─────────────────────────────────────────
/**
 * A PopCart style served by our API (GET /api/v1/maps/tiles.json and /maps/fonts), so the map
 * works without anyone else's service and no map request leaves our servers. The API redirects to
 * OpenFreeMap until its tile file exists, so this is safe on any server. Without an API origin the
 * style keeps OpenFreeMap.
 */
export function styleFromApi(style: StyleSpecification, apiOrigin: string | null | undefined): StyleSpecification {
  const origin = (apiOrigin ?? '').replace(/\/+$/, '');
  if (!origin) return style;
  return {
    ...style,
    glyphs: `${origin}/api/v1/maps/fonts/{fontstack}/{range}.pbf`,
    sources: {
      ...style.sources,
      openmaptiles: { type: 'vector', url: `${origin}/api/v1/maps/tiles.json`, attribution: '© OpenMapTiles © OpenStreetMap contributors' },
    },
  };
}
